import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Pill, ChevronDown, Info } from "lucide-react";
import { calculateMATAnalysis } from "./rcCalculations";

const RCMATAnalysisTab = ({ organizationId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRiskFactor, setSelectedRiskFactor] = useState("trauma");
  const [isRiskFactorDropdownOpen, setIsRiskFactorDropdownOpen] =
    useState(false);

  const [matData, setMatData] = useState({
    matOverview: [],
    matTimeEffects: {
      earlyRecovery: { mat: {}, nonMat: {} },
      midRecovery: { mat: {}, nonMat: {} },
      establishedRecovery: { mat: {}, nonMat: {} },
      longTermRecovery: { mat: {}, nonMat: {} },
    },
    traumaInteraction: {
      matTrauma: {},
      matNoTrauma: {},
      noMatTrauma: {},
      noMatNoTrauma: {},
    },
    domainImpacts: [],
  });

  const riskFactorOptions = [
    {
      id: "trauma",
      name: "Childhood Trauma",
      withLabel: "MAT + Trauma",
      withoutLabel: "MAT + No Trauma",
    },
    {
      id: "homeless",
      name: "Homeless ≥1 Year",
      withLabel: "MAT + Homeless",
      withoutLabel: "MAT + No Homeless",
    },
    {
      id: "prison",
      name: "Prison History",
      withLabel: "MAT + Prison",
      withoutLabel: "MAT + No Prison",
    },
    {
      id: "diagnosis",
      name: "Co-occurring Diagnosis",
      withLabel: "MAT + Co-occurring",
      withoutLabel: "MAT + No Co-occurring",
    },
    {
      id: "kids",
      name: "Has Children",
      withLabel: "MAT + Children",
      withoutLabel: "MAT + No Children",
    },
    {
      id: "therapy",
      name: "In Therapy",
      withLabel: "MAT + Therapy",
      withoutLabel: "MAT + No Therapy",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!organizationId) {
          throw new Error("Organization ID is required");
        }

        setLoading(true);

        // Use the new MAT analysis function
        const data = await calculateMATAnalysis(organizationId);
        setMatData(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="h-10 w-1 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full" />
        <h2 className="text-2xl font-bold text-white">
          Medication-Assisted Treatment (MAT) Analysis
        </h2>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <h3 className="text-xl font-bold text-amber-400 mb-4">
          MAT Impact Overview
        </h3>
        {/* Add this Info tooltip here */}
        <span className="absolute top-2 right-2 z-10 group">
          <Info
            size={16}
            className="text-slate-400 cursor-pointer hover:text-slate-300 transition-colors"
          />
          <span className="pointer-events-none absolute right-0 top-full mt-2 w-80 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
            <div className="space-y-2">
              <div>
                <strong className="text-amber-400">What this shows:</strong>{" "}
                Comparison of sobriety outcomes and Recovery Capital scores
                between residents who use Medication-Assisted Treatment (MAT)
                versus those who don't.
              </div>
              <div>
                <strong className="text-blue-400">Questions it answers:</strong>{" "}
                "How do MAT users compare to non-MAT users in terms of staying
                sober?" and "Do MAT users develop similar recovery capital
                despite different treatment approaches?"
              </div>
              <div>
                <strong className="text-green-400">Key insight:</strong> MAT
                users achieve nearly identical RC scores despite typically
                having higher baseline risk factors and shorter time in recovery
                at assessment.
              </div>
              <div>
                <strong className="text-purple-400">Data source:</strong>{" "}
                Sobriety rates from program graduates only (alumni), RC scores
                from all residents (current + alumni).
              </div>
            </div>
          </span>
        </span>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={matData.matOverview}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.1)"
                />
                <XAxis dataKey="name" stroke="#94a3b8" />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  stroke="#f59e0b"
                  domain={[0, 80]}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#3b82f6"
                  domain={[145, 155]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: "0.5rem",
                  }}
                  itemStyle={{ color: "#e2e8f0" }}
                  labelStyle={{ color: "#f8fafc", fontWeight: "bold" }}
                  formatter={(value, name, props) => {
                    if (name === "Sobriety Rate %") {
                      const { soberCount, totalAlumni } = props.payload;
                      return [
                        `${value}% (${soberCount}/${totalAlumni} alumni)`,
                        "Sobriety Rate",
                      ];
                    } else if (name === "RC Score") {
                      const { totalUsers } = props.payload;
                      return [
                        `${value} (${totalUsers} with assessments)`, // Changed label
                        "RC Score",
                      ];
                    }
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="sobriety"
                  name="Sobriety Rate %"
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  yAxisId="right"
                  dataKey="rc"
                  name="RC Score"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-semibold mb-3">Key Observations</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-2">
                <div className="mt-0.5 p-1 rounded-full bg-amber-500/20 flex-shrink-0">
                  <Pill size={14} className="text-amber-400" />
                </div>
                <span className="text-slate-300">
                  Similar overall RC scores between MAT (
                  {matData.matOverview[0]?.rc}) and non-MAT (
                  {matData.matOverview[1]?.rc}) groups
                </span>
              </li>
              <li className="flex gap-2">
                <div className="mt-0.5 p-1 rounded-full bg-amber-500/20 flex-shrink-0">
                  <Pill size={14} className="text-amber-400" />
                </div>
                <span className="text-slate-300">
                  {matData.matOverview[0]?.sobriety <
                  matData.matOverview[1]?.sobriety
                    ? "Lower"
                    : "Higher"}{" "}
                  sobriety rates among MAT users (
                  {matData.matOverview[0]?.sobriety}%) compared to non-MAT (
                  {matData.matOverview[1]?.sobriety}%)
                </span>
              </li>
              <li className="flex gap-2">
                <div className="mt-0.5 p-1 rounded-full bg-amber-500/20 flex-shrink-0">
                  <Pill size={14} className="text-amber-400" />
                </div>
                <span className="text-slate-300">
                  MAT users typically have fewer days in recovery at assessment
                  (median: 164 vs. 208 days)
                </span>
              </li>
              <li className="flex gap-2">
                <div className="mt-0.5 p-1 rounded-full bg-amber-500/20 flex-shrink-0">
                  <Pill size={14} className="text-amber-400" />
                </div>
                <span className="text-slate-300">
                  RC scores are virtually identical despite MAT users having
                  higher baseline risk
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 relative">
          <h3 className="text-xl font-bold text-amber-400 mb-4">
            MAT Time-Dependent Effects
          </h3>

          {/* Position the Info icon in the top-right corner of the entire section */}
          <span className="absolute top-6 right-6 z-10 group">
            <Info
              size={16}
              className="text-slate-400 cursor-pointer hover:text-slate-300 transition-colors"
            />
            <span className="pointer-events-none absolute right-0 top-full mt-2 w-80 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
              <div className="space-y-2">
                <div>
                  <strong className="text-amber-400">What this shows:</strong>{" "}
                  Period-specific relapse rates for MAT vs non-MAT users during
                  each recovery phase (0-3m, 3-6m, 6-12m, 12m+).
                </div>
                <div>
                  <strong className="text-blue-400">
                    Questions it answers:
                  </strong>{" "}
                  "What's my relapse risk if I'm on MAT and currently at 6
                  months sober?" or "Which recovery phases are most vulnerable
                  for MAT users?"
                </div>
                <div>
                  <strong className="text-purple-400">Key difference:</strong>{" "}
                  Shows risk during each phase vs. Progression tab which shows
                  when relapses occur overall.
                </div>
                <div>
                  <strong className="text-red-400">Sample sizes:</strong> Small
                  groups in later phases - interpret with caution (especially 6+
                  months).
                </div>
              </div>
            </span>
          </span>
          <div className="space-y-4">
            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                Early Recovery (0-90 days)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">MAT Group</p>
                  <p className="text-lg text-white">
                    {matData.matTimeEffects.earlyRecovery?.mat.relapseRate}%
                    relapse
                  </p>
                  <p className="text-sm text-slate-400">
                    {matData.matTimeEffects.earlyRecovery?.mat.rcAvg} avg RC
                  </p>
                  <p className="text-xs text-slate-500">
                    n = {matData.matTimeEffects.earlyRecovery?.mat.count}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Non-MAT Group</p>
                  <p className="text-lg text-white">
                    {matData.matTimeEffects.earlyRecovery?.nonMat.relapseRate}%
                    relapse
                  </p>
                  <p className="text-sm text-slate-400">
                    {matData.matTimeEffects.earlyRecovery?.nonMat.rcAvg} avg RC
                  </p>
                  <p className="text-xs text-slate-500">
                    n = {matData.matTimeEffects.earlyRecovery?.nonMat.count}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                Mid-Recovery (91-180 days)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">MAT Group</p>
                  <p className="text-lg text-white">
                    {matData.matTimeEffects.midRecovery?.mat.relapseRate}%
                    relapse
                  </p>
                  <p className="text-sm text-slate-400">
                    {matData.matTimeEffects.midRecovery?.mat.rcAvg} avg RC
                  </p>
                  <p className="text-xs text-slate-500">
                    n = {matData.matTimeEffects.midRecovery?.mat.count}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Non-MAT Group</p>
                  <p className="text-lg text-white">
                    {matData.matTimeEffects.midRecovery?.nonMat.relapseRate}%
                    relapse
                  </p>
                  <p className="text-sm text-slate-400">
                    {matData.matTimeEffects.midRecovery?.nonMat.rcAvg} avg RC
                  </p>
                  <p className="text-xs text-slate-500">
                    n = {matData.matTimeEffects.midRecovery?.nonMat.count}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                Established (181-365 days)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">MAT Group</p>
                  <p className="text-lg text-white">
                    {
                      matData.matTimeEffects.establishedRecovery?.mat
                        .relapseRate
                    }
                    % relapse
                  </p>
                  <p className="text-sm text-slate-400">
                    {matData.matTimeEffects.establishedRecovery?.mat.rcAvg} avg
                    RC
                  </p>
                  <p className="text-xs text-slate-500">
                    n = {matData.matTimeEffects.establishedRecovery?.mat.count}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Non-MAT Group</p>
                  <p className="text-lg text-white">
                    {
                      matData.matTimeEffects.establishedRecovery?.nonMat
                        .relapseRate
                    }
                    % relapse
                  </p>
                  <p className="text-sm text-slate-400">
                    {matData.matTimeEffects.establishedRecovery?.nonMat.rcAvg}{" "}
                    avg RC
                  </p>
                  <p className="text-xs text-slate-500">
                    n ={" "}
                    {matData.matTimeEffects.establishedRecovery?.nonMat.count}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                Long-Term (366+ days)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">MAT Group</p>
                  <p className="text-lg text-white">
                    {matData.matTimeEffects.longTermRecovery?.mat.relapseRate}%
                    relapse
                  </p>
                  <p className="text-sm text-slate-400">
                    {matData.matTimeEffects.longTermRecovery?.mat.rcAvg} avg RC
                  </p>
                  <p className="text-xs text-slate-500">
                    n = {matData.matTimeEffects.longTermRecovery?.mat.count}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Non-MAT Group</p>
                  <p className="text-lg text-white">
                    {
                      matData.matTimeEffects.longTermRecovery?.nonMat
                        .relapseRate
                    }
                    % relapse
                  </p>
                  <p className="text-sm text-slate-400">
                    {matData.matTimeEffects.longTermRecovery?.nonMat.rcAvg} avg
                    RC
                  </p>
                  <p className="text-xs text-slate-500">
                    n = {matData.matTimeEffects.longTermRecovery?.nonMat.count}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-amber-400">
              MAT and Risk/Protective Factor Influence
            </h3>

            {/* Risk Factor Selection Dropdown */}
            <div className="relative">
              <button
                onClick={() =>
                  setIsRiskFactorDropdownOpen(!isRiskFactorDropdownOpen)
                }
                className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg px-4 py-2 text-sm transition-all"
              >
                <span>
                  {riskFactorOptions.find(
                    (opt) => opt.id === selectedRiskFactor
                  )?.name || "Select Factor"}
                </span>
                <ChevronDown size={16} />
              </button>

              {isRiskFactorDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-10">
                  <ul className="py-1">
                    {riskFactorOptions.map((factor) => (
                      <li key={factor.id}>
                        <button
                          onClick={() => {
                            setSelectedRiskFactor(factor.id);
                            setIsRiskFactorDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            selectedRiskFactor === factor.id
                              ? "bg-slate-700 text-white"
                              : "text-slate-300 hover:bg-slate-700 hover:text-white"
                          }`}
                        >
                          {factor.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Display the selected risk factor data */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                {
                  riskFactorOptions.find((opt) => opt.id === selectedRiskFactor)
                    ?.withLabel
                }
              </h4>
              <div className="mb-2">
                <p className="text-2xl font-bold text-red-400">
                  {
                    matData.allRiskFactorInteractions?.[selectedRiskFactor]
                      ?.matWith?.relapseRate
                  }
                  %
                </p>
                <p className="text-xs text-slate-400">Relapse Rate</p>
              </div>
              <div className="mb-2">
                <p className="text-2xl font-bold text-white">
                  {
                    matData.allRiskFactorInteractions?.[selectedRiskFactor]
                      ?.matWith?.rcAvg
                  }
                </p>
                <p className="text-xs text-slate-400">Average RC Score</p>
              </div>
              {/* ADD THIS COUNT SECTION */}
              <div className="mt-2 pt-2 border-t border-slate-700">
                <p className="text-sm text-slate-300">
                  n ={" "}
                  {matData.allRiskFactorInteractions?.[selectedRiskFactor]
                    ?.matWith?.count || 0}
                </p>
              </div>
            </div>

            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                {
                  riskFactorOptions.find((opt) => opt.id === selectedRiskFactor)
                    ?.withoutLabel
                }
              </h4>
              <div className="mb-2">
                <p className="text-2xl font-bold text-green-400">
                  {
                    matData.allRiskFactorInteractions?.[selectedRiskFactor]
                      ?.matWithout?.relapseRate
                  }
                  %
                </p>
                <p className="text-xs text-slate-400">Relapse Rate</p>
              </div>
              <div className="mb-2">
                <p className="text-2xl font-bold text-white">
                  {
                    matData.allRiskFactorInteractions?.[selectedRiskFactor]
                      ?.matWithout?.rcAvg
                  }
                </p>
                <p className="text-xs text-slate-400">Average RC Score</p>
              </div>
              {/* ADD THIS COUNT SECTION */}
              <div className="mt-2 pt-2 border-t border-slate-700">
                <p className="text-sm text-slate-300">
                  n ={" "}
                  {matData.allRiskFactorInteractions?.[selectedRiskFactor]
                    ?.matWithout?.count || 0}
                </p>
              </div>
            </div>

            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                No MAT +{" "}
                {selectedRiskFactor.charAt(0).toUpperCase() +
                  selectedRiskFactor.slice(1)}
              </h4>
              <div className="mb-2">
                <p className="text-2xl font-bold text-amber-400">
                  {
                    matData.allRiskFactorInteractions?.[selectedRiskFactor]
                      ?.noMatWith?.relapseRate
                  }
                  %
                </p>
                <p className="text-xs text-slate-400">Relapse Rate</p>
              </div>
              <div className="mb-2">
                <p className="text-2xl font-bold text-white">
                  {
                    matData.allRiskFactorInteractions?.[selectedRiskFactor]
                      ?.noMatWith?.rcAvg
                  }
                </p>
                <p className="text-xs text-slate-400">Average RC Score</p>
              </div>
              {/* ADD THIS COUNT SECTION */}
              <div className="mt-2 pt-2 border-t border-slate-700">
                <p className="text-sm text-slate-300">
                  n ={" "}
                  {matData.allRiskFactorInteractions?.[selectedRiskFactor]
                    ?.noMatWith?.count || 0}
                </p>
              </div>
            </div>

            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                No MAT + No{" "}
                {selectedRiskFactor.charAt(0).toUpperCase() +
                  selectedRiskFactor.slice(1)}
              </h4>
              <div className="mb-2">
                <p className="text-2xl font-bold text-green-400">
                  {
                    matData.allRiskFactorInteractions?.[selectedRiskFactor]
                      ?.noMatWithout?.relapseRate
                  }
                  %
                </p>
                <p className="text-xs text-slate-400">Relapse Rate</p>
              </div>
              <div className="mb-2">
                <p className="text-2xl font-bold text-white">
                  {
                    matData.allRiskFactorInteractions?.[selectedRiskFactor]
                      ?.noMatWithout?.rcAvg
                  }
                </p>
                <p className="text-xs text-slate-400">Average RC Score</p>
              </div>
              {/* ADD THIS COUNT SECTION */}
              <div className="mt-2 pt-2 border-t border-slate-700">
                <p className="text-sm text-slate-300">
                  n ={" "}
                  {matData.allRiskFactorInteractions?.[selectedRiskFactor]
                    ?.noMatWithout?.count || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Keep your existing domain impacts section */}
          <div className="bg-slate-900/50 p-4 rounded-lg mt-4">
            <h4 className="text-white font-semibold mb-2">
              MAT Domain Impacts
            </h4>
            <div className="space-y-2">
              {matData.domainImpacts.map((impact, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-slate-300 text-sm">
                    {impact.domain}
                  </span>
                  <span
                    className={`${
                      impact.percentDiff >= 0
                        ? "text-green-400"
                        : "text-red-400"
                    } text-sm`}
                  >
                    {impact.percentDiff >= 0 ? "+" : ""}
                    {impact.percentDiff}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RCMATAnalysisTab;
